import { cn } from '@/lib/utils';
import { createContext, PropsWithChildren, useContext, useMemo, useState } from 'react';

export interface StepperContextProps {
  currentStep: number;
  onNext: () => void;
  onPrev: () => void;
}

const StepperContext = createContext<StepperContextProps | null>(null);

export function useStepper() {
  const context = useContext(StepperContext);
  if (!context) throw new Error("useStepper must be used within <Stepper>");
  return context;
}

export function Stepper({ children }: PropsWithChildren) {
  const [currentStep, setCurrentStep] = useState<number>(1); // 1-indexed
  const onNext = () => setCurrentStep(prev => prev + 1);
  const onPrev = () => setCurrentStep(prev => prev === 1 ? prev : prev - 1);

  const contextValue = useMemo<StepperContextProps>(() => ({
    currentStep,
    onNext,
    onPrev,
  }), [currentStep, onNext, onPrev]);

  return (
    <StepperContext.Provider value={contextValue}>
      {children}
    </StepperContext.Provider>
  );
}

interface StepProps extends PropsWithChildren {
  step: number;
}

export function Step({ step, children }: StepProps) {
  const { currentStep } = useStepper();

  return (
    <div className={cn(step === currentStep ? "block" : "hidden")}>
      {children}
    </div>
  );
}
